import React, {useContext, useMemo, useState} from "react"
import './scss/App.scss'
import {
    QueryClient,
    QueryClientProvider,
} from "react-query"
import {
    BrowserRouter as Router,
} from "react-router-dom"
import Content from "./Content"
import LocaleWrapper, {LocaleContext} from "./LocaleContext"
import {
    AuthCheck,
    GetCustomerSegment,
    GetInstanceLanguage,
    GetLanguageOptions,
    GetUser,
    GetUserSurvey
} from "./api/APISurvey";
import {ReactComponent as Logo, ReactComponent as CloudLogo} from "./svg/logo-green.svg";
import {useIntl} from "react-intl";
import {GetSupportBotEnabled} from "./api/APIBot";

export const queryClient = new QueryClient()

const AuthContext = React.createContext({
    authContext: {},
    setAuthContext: () => {}
})

function AuthProvider({children, authContext}) {
    const [authData, setAuthData] = useState(authContext)
    const value = useMemo(() =>(
        {authData, setAuthData}
    ), [authData])
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuthData() {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuthData must be used within an AuthProvider')
    }
    return context
}

const LanguageContext = React.createContext({
    languageContext: {},
    setLanguageContext: () => {}
})

function LanguageProvider({children, context}) {
    const [instanceLang, setInstanceLang] = useState(context)
    const value = useMemo(() =>(
        {instanceLang, setInstanceLang}
    ), [instanceLang])
    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguageData() {
    const context = useContext(LanguageContext)
    if (context === undefined) {
        throw new Error('useLanguageData must be used within a LanguageProvider')
    }
    return context
}

const LanguageOptionsContext = React.createContext({
    languageOptionsContext: {},
    setLanguageOptionsContext: () => {}
})

function LanguageOptionsProvider({children, context}) {
    const [instanceLangOptions, setInstanceLangOptions] = useState(context)
    const value = useMemo(() =>(
        {instanceLangOptions, setInstanceLangOptions}
    ), [instanceLangOptions])
    return <LanguageOptionsContext.Provider value={value}>{children}</LanguageOptionsContext.Provider>
}

export function useLanguageOptionsData() {
    const context = useContext(LanguageOptionsContext)
    if (context === undefined) {
        throw new Error('useLanguageOptionsData must be used within a LanguageOptionsProvider')
    }
    return context
}

const SupportBotEnabledContext = React.createContext({
    supportBotEnabledContext: {},
    setSupportBotEnabledContext: () => {}
})

function SupportBotEnabledProvider({children, context = ""}) {
    const [supportBotEnabled, setSupportBotEnabled] = useState(context)
    const value = useMemo(() =>(
        {supportBotEnabled, setSupportBotEnabled}
    ), [supportBotEnabled])
    return <SupportBotEnabledContext.Provider value={value}>{children}</SupportBotEnabledContext.Provider>
}

export function useSupportBotEnabledData() {
    const context = useContext(SupportBotEnabledContext)
    if (context === undefined) {
        throw new Error('useCustomerSegmentData must be used within a CustomerSegmentProvider')
    }
    return context
}

const CustomerSegmentContext = React.createContext({
    customerSegmentContext: {},
    setCustomerSegmentContext: () => {}
})

function CustomerSegmentProvider({children, context = ""}) {
    const [customerSegment, setCustomerSegment] = useState(context)
    const value = useMemo(() =>(
        {customerSegment, setCustomerSegment}
    ), [customerSegment])
    return <CustomerSegmentContext.Provider value={value}>{children}</CustomerSegmentContext.Provider>
}

export function useCustomerSegmentData() {
    const context = useContext(CustomerSegmentContext)
    if (context === undefined) {
        throw new Error('useCustomerSegmentData must be used within a CustomerSegmentProvider')
    }
    return context
}

const CurrentUserContext = React.createContext({
    currentUserContext: {},
    setCurrentUserContext: () => {}
})

function CurrentUserProvider({children, userContext}) {
    const [currentUserData, setCurrentUserData] = useState(userContext)
    const value = useMemo(() =>(
        {currentUserData, setCurrentUserData}
    ), [currentUserData])
    return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>
}

export function useCurrentUserData() {
    const context = useContext(CurrentUserContext)
    if (context === undefined) {
        throw new Error('useCurrentUserData must be used within an CurrentUserProvider')
    }
    return context
}

function App() {
    return <QueryClientProvider client={queryClient}>
        <Auth />
    </QueryClientProvider>
}

function Auth() {
    const authContext = window.location.hostname === "localhost" ?
        {
            status:"success",
            data:{
                "logouturl":"https://dev.annieadvisor.com/simplesaml/module.php/core/as_logout.php?AuthId=sp-google&ReturnTo=https%3A%2F%2Fdev.annieadvisor.com%2Fapi%2Fauth.php",
                "firstname":"localhost",
                "lastname":"developer",
                "uid":"devannie",
                "status":"OK"
            }
        }
        : AuthCheck()

    const success = authContext.status === "success" && authContext.data.hasOwnProperty("uid") && authContext.data.hasOwnProperty("status") && authContext.data.status === "OK"
    const error = authContext.status === "error"

    if (success) {
        return <AuthSuccess authContext={authContext.data}/>
    }
    if (error) {
        if (typeof authContext.error.response.data.loginURL !== undefined) {
            window.location.href = authContext.error.response.data.loginURL
        } else {
            return <AuthError />
        }
    }

    return <Loading />
}

function AuthSuccess({authContext}) {
    const getUser = GetUser(authContext.uid)
    const getUserSurveys = GetUserSurvey()
    const getInstanceLanguage = GetInstanceLanguage()
    const getLanguageOptions = GetLanguageOptions()
    const getCustomerSegment = GetCustomerSegment()
    const getSupportBotEnabled = GetSupportBotEnabled()

    const loading = getUser.isLoading || getUserSurveys.isLoading || getInstanceLanguage.isLoading || getLanguageOptions.isLoading || getCustomerSegment.isLoading || getSupportBotEnabled.isLoading
    const error = getUser.isError || getUserSurveys.isError || getInstanceLanguage.isError || getLanguageOptions.isError || getCustomerSegment.isError || getSupportBotEnabled.isError
    const unAuthorized = error && getUserSurveys.error?.response?.status === 401

    let content

    if (loading) {
        return <Loading />
    }

    if (error) {
        if (unAuthorized) {
            content = <SomethingWentWrong errorType={"NO_USAGE_RIGHTS"} />
        } else {
            content = <SomethingWentWrong errorType={"LOADING_ERROR"} />
        }
    } else {
        content = <InstanceWrapper languageContext={getInstanceLanguage?.data[0]?.value} languageOptions={getLanguageOptions?.data[0]?.value} customerSegment={getCustomerSegment?.data[0]?.value} supportBotEnabled={getSupportBotEnabled?.data[0]?.value}>
            <CurrentUserProvider userContext={getUser.data[0]}>
                <Router basename={"/admin"}>
                    <div className={"app"}>
                        <Content />
                    </div>
                </Router>
            </CurrentUserProvider>
        </InstanceWrapper>
    }

    return <AuthProvider authContext={authContext}>
        <LocaleWrapper language={getInstanceLanguage?.data[0]?.value || ""} languageOptions={getLanguageOptions?.data[0]?.value || []}>
            {content}
        </LocaleWrapper>
    </AuthProvider>
}

function InstanceWrapper({children, languageContext, languageOptions, customerSegment, supportBotEnabled}) {
    return <LanguageProvider context={languageContext}>
        <LanguageOptionsProvider context={languageOptions}>
            <CustomerSegmentProvider context={customerSegment}>
                <SupportBotEnabledProvider context={supportBotEnabled}>
                    {children}
                </SupportBotEnabledProvider>
            </CustomerSegmentProvider>
        </LanguageOptionsProvider>
    </LanguageProvider>
}

function SomethingWentWrong({errorType}) {
    const intl = useIntl()
    return <div className={"app"}>
        <div className={"content center login"}>
            <div>
                <div className={"logo"}>
                    <CloudLogo />
                </div>
                {
                    errorType === "NO_USAGE_RIGHTS" &&
                    <h1>
                        {intl.formatMessage({
                            id:"error.notSuperadminOrCoordinator",
                            defaultMessage:"Sorry. Only administrators and coordinators are allowed to view this page."
                        })}
                    </h1>
                }
                {
                    errorType === "LOADING_ERROR" &&
                    <h1>
                        {intl.formatMessage({
                            id:"error.loading",
                            defaultMessage:"Loading error. Can't access database. Please try again later."
                        })}
                    </h1>
                }
            </div>
        </div>
    </div>
}

function AuthError() {
    const hostname = window.location.hostname
    const instanceEmail = hostname.indexOf(".") > -1 ? hostname.substring( 0, hostname.indexOf(".") ) +"@annieadvisor.com" : hostname +"@annieadvisor.com"
    return <div className={"app"}>
        <div className={"content center login"}>
            <div>
                <div className={"logo"}>
                    <CloudLogo />
                </div>
                <h1>Sorry. Seems like an authentication error.</h1>
                <p>Please contact <a href={"mailto:"+instanceEmail}>{instanceEmail}</a></p>
            </div>
        </div>
    </div>
}

export function Loading() {
    return <div className={"app"}>
        <div className={"content center login"}>
            <div className={"loader"}>
                <Logo />
                <div className={"loader-container"}>
                    <div className={"loader-bar"}/>
                </div>
            </div>
        </div>
    </div>
}

export default App;